import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, message, Popconfirm, Space, Switch, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BarChartOutlined, MessageOutlined } from '@ant-design/icons';
import { batchApi } from '../../services/api';
import type { Batch, CreateBatchRequest, User } from '../../types';
import BatchStatsModal from '../../components/BatchStatsModal';
import BatchMessagesModal from '../../components/BatchMessagesModal';
import dayjs from 'dayjs';

const { TextArea } = Input;

const Batches: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBatch, setEditingBatch] = useState<Batch | null>(null);
  const [statsModalVisible, setStatsModalVisible] = useState(false);
  const [selectedBatchForStats, setSelectedBatchForStats] = useState<Batch | null>(null);
  const [messagesModalVisible, setMessagesModalVisible] = useState(false);
  const [selectedBatchForMessages, setSelectedBatchForMessages] = useState<Batch | null>(null);
  const [form] = Form.useForm();

  // Check if current user is superadmin
  const currentUser: User | null = JSON.parse(localStorage.getItem('user') || 'null');
  const isSuperAdmin = currentUser?.role === 'superadmin';

  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = async () => {
    setLoading(true);
    try {
      const data = await batchApi.list();
      setBatches(data);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingBatch(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (batch: Batch) => {
    setEditingBatch(batch);
    form.setFieldsValue({
      ...batch,
      numbers: (batch.numbers || []).join('\n'),
      apiParamsId: batch.apiParams.id,
      apiParamsBillgroup: batch.apiParams.billgroup,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await batchApi.delete(id);
      message.success('Batch deleted successfully');
      loadBatches();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to delete batch');
    }
  };

  const handleViewStats = (batch: Batch) => {
    setSelectedBatchForStats(batch);
    setStatsModalVisible(true);
  };

  const handleViewMessages = (batch: Batch) => {
    setSelectedBatchForMessages(batch);
    setMessagesModalVisible(true);
  };

  const handleToggleEnabled = async (batch: Batch, enabled: boolean) => {
    try {
      await batchApi.toggleEnabled(batch.id, enabled);
      message.success(`Batch ${enabled ? 'enabled' : 'disabled'} successfully`);
      loadBatches();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to toggle batch status');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const numbers = values.numbers
        .split('\n')
        .map((n: string) => n.trim())
        .filter((n: string) => n.length > 0);

      const data: CreateBatchRequest = {
        name: values.name,
        country: values.country,
        numbers,
        apiUrl: values.apiUrl || 'http://188.166.65.252/api/showmsgs.php',
        apiParams: {
          id: values.apiParamsId,
          billgroup: values.apiParamsBillgroup,
        },
        allocationMonth: values.allocationMonth,
        useCase: values.useCase,
      };

      if (editingBatch) {
        await batchApi.update(editingBatch.id, data);
        message.success('Batch updated successfully');
      } else {
        await batchApi.create(data);
        message.success('Batch created successfully');
      }

      setModalVisible(false);
      form.resetFields();
      loadBatches();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to save batch');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
    },
    {
      title: 'Numbers',
      dataIndex: 'numbers',
      key: 'numbers',
      render: (numbers: string[]) => numbers?.length || 0,
    },
    {
      title: 'Use Case',
      dataIndex: 'useCase',
      key: 'useCase',
    },
    {
      title: 'Allocation Month',
      dataIndex: 'allocationMonth',
      key: 'allocationMonth',
    },
    {
      title: 'Status',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled: boolean, record: Batch) => (
        isSuperAdmin ? (
          <Switch
            checked={enabled}
            onChange={(checked) => handleToggleEnabled(record, checked)}
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
          />
        ) : (
          <Tag color={enabled ? 'green' : 'red'}>
            {enabled ? 'Enabled' : 'Disabled'}
          </Tag>
        )
      ),
    },
    {
      title: 'Last Sync',
      dataIndex: 'lastSyncAt',
      key: 'lastSyncAt',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : 'Never',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Batch) => (
        <Space>
          <Button
            type="link"
            icon={<MessageOutlined />}
            onClick={() => handleViewMessages(record)}
          >
            Messages
          </Button>
          <Button
            type="link"
            icon={<BarChartOutlined />}
            onClick={() => handleViewStats(record)}
          >
            Stats
          </Button>
          {isSuperAdmin && (
            <>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              >
                Edit
              </Button>
              <Popconfirm
                title="Are you sure you want to delete this batch?"
                onConfirm={() => handleDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button type="link" danger icon={<DeleteOutlined />}>
                  Delete
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1>Batch Management</h1>
        {isSuperAdmin && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Create Batch
          </Button>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={batches}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} batches`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
      />

      <Modal
        title={editingBatch ? 'Edit Batch' : 'Create Batch'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Batch Name"
            rules={[{ required: true, message: 'Please enter batch name' }]}
          >
            <Input placeholder="e.g., Australia Nov 2024" />
          </Form.Item>

          <Form.Item
            name="country"
            label="Country"
            rules={[{ required: true, message: 'Please enter country' }]}
          >
            <Input placeholder="e.g., Australia" />
          </Form.Item>

          <Form.Item
            name="numbers"
            label="Phone Numbers (one per line)"
            rules={[{ required: true, message: 'Please enter phone numbers' }]}
          >
            <TextArea
              rows={8}
              placeholder="+61400000001&#10;+61400000002&#10;+61400000003"
            />
          </Form.Item>

          <Form.Item
            name="apiUrl"
            label="API URL"
            initialValue="http://188.166.65.252/api/showmsgs.php"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="apiParamsId"
            label="API ID Parameter"
            rules={[{ required: true, message: 'Please enter API ID' }]}
          >
            <Input placeholder="e.g., XYZ" />
          </Form.Item>

          <Form.Item
            name="apiParamsBillgroup"
            label="API Billgroup Parameter"
            rules={[{ required: true, message: 'Please enter billgroup' }]}
          >
            <Input placeholder="e.g., 1712" />
          </Form.Item>

          <Form.Item
            name="allocationMonth"
            label="Allocation Month"
          >
            <Input placeholder="e.g., Nov 2024" />
          </Form.Item>

          <Form.Item
            name="useCase"
            label="Use Case"
          >
            <Input placeholder="e.g., Google Only" />
          </Form.Item>
        </Form>
      </Modal>

      <BatchStatsModal
        visible={statsModalVisible}
        batch={selectedBatchForStats}
        onClose={() => setStatsModalVisible(false)}
        isSuperAdmin={isSuperAdmin}
      />

      <BatchMessagesModal
        visible={messagesModalVisible}
        batch={selectedBatchForMessages}
        onClose={() => setMessagesModalVisible(false)}
      />
    </div>
  );
};

export default Batches;
